import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { useConfig } from "../hooks/useConfig";

/* ---------- Toasts ---------- */

type Toast = { id: number; text: string; kind: "ok" | "err" };
const ToastCtx = createContext<(text: string, kind?: Toast["kind"]) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: Toast["kind"] = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

/* ---------- Page scaffolding ---------- */

export function Page({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <>
      <div className="page-head">
        <h1>{title}</h1>
        <div className="page-actions">{actions}</div>
      </div>
      {children}
    </>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="toolbar">{children}</div>;
}

export function SearchBox({ value, onChange, placeholder = "Search…" }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [local, setLocal] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => onChange(local), 350);
    return () => clearTimeout(t);
  }, [local, onChange]);
  return <input className="input" type="search" placeholder={placeholder} value={local} onChange={(e) => setLocal(e.target.value)} />;
}

/* ---------- Table ---------- */

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  width?: string;
  align?: "left" | "right";
}

export function DataTable<T extends { id: string }>({
  columns,
  rows,
  loading,
  error,
  empty = "Nothing here yet",
  onRowClick,
}: {
  columns: Column<T>[];
  rows: T[];
  loading: boolean;
  error: string | null;
  empty?: string;
  onRowClick?: (row: T) => void;
}) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ width: c.width, textAlign: c.align }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {error && (
            <tr>
              <td colSpan={columns.length} className="error">
                {error}
              </td>
            </tr>
          )}
          {!error && !loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="muted">
                {empty}
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={row.id} className={onRowClick ? "clickable" : undefined} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align }}>
                  {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {loading && <div className="table-loading">Loading…</div>}
    </div>
  );
}

export function Pagination({ page, perPage, total, onPage }: { page: number; perPage: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages <= 1) return null;
  return (
    <div className="pagination">
      <button type="button" className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ‹ Prev
      </button>
      <span>
        Page {page} of {pages} · {total} rows
      </span>
      <button type="button" className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next ›
      </button>
    </div>
  );
}

/* ---------- Display helpers ---------- */

export function Money({ value }: { value: string | number | null | undefined }) {
  const config = useConfig();
  const n = typeof value === "number" ? value : Number.parseFloat(value ?? "0");
  const sym = config?.currency.symbol ?? "";
  return (
    <span className="mono">
      {n < 0 ? "-" : ""}
      {sym}
      {Math.abs(Number.isNaN(n) ? 0 : n).toFixed(2)}
    </span>
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "ok" | "warn" | "bad" | "info" }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function StatusBadge({ status }: { status: string }) {
  return (
    <span className="badge" style={{ background: `var(--c-status-${status}, #6b7280)`, color: "#fff" }}>
      {status}
    </span>
  );
}

export function Bool({ value }: { value: boolean }) {
  return <Badge tone={value ? "ok" : "neutral"}>{value ? "yes" : "no"}</Badge>;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/* ---------- Forms ---------- */

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <label className="switch">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="switch-track" />
      {label && <span>{label}</span>}
    </label>
  );
}

/* ---------- Modal ---------- */

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? " modal-wide" : ""}`} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function useConfirm() {
  const [state, setState] = useState<{ text: string; resolve: (ok: boolean) => void } | null>(null);
  const confirm = useCallback((text: string) => new Promise<boolean>((resolve) => setState({ text, resolve })), []);
  const dialog = useMemo(
    () =>
      state ? (
        <Modal title="Please confirm" onClose={() => (state.resolve(false), setState(null))}>
          <p>{state.text}</p>
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => (state.resolve(false), setState(null))}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" onClick={() => (state.resolve(true), setState(null))}>
              Confirm
            </button>
          </div>
        </Modal>
      ) : null,
    [state],
  );
  return { confirm, dialog };
}

/* ---------- Tiny bar chart ---------- */

export function Bars({ points, valueOf, labelOf, format }: { points: { day: string }[]; valueOf: (p: { day: string }) => number; labelOf?: (p: { day: string }) => string; format?: (n: number) => ReactNode }) {
  const max = Math.max(1, ...points.map(valueOf));
  return (
    <div className="bars">
      {points.map((p) => {
        const v = valueOf(p);
        return (
          <div key={p.day} className="bar-row">
            <span className="bar-label">{labelOf ? labelOf(p) : p.day.slice(5)}</span>
            <span className="bar-track">
              <span className="bar-fill" style={{ width: `${(v / max) * 100}%` }} />
            </span>
            <span className="bar-value">{format ? format(v) : v}</span>
          </div>
        );
      })}
    </div>
  );
}
